/**
 * The button for actions that happen in place. Links that navigate use `LinkButton`, which shares
 * this look through `buttonStyle.ts`.
 *
 * Five variants. `primary` is the one main action on a screen, `secondary` and `tertiary` the rest,
 * and anything that removes, ends or deletes is `danger` or `dangerSecondary`, never `primary`.
 *
 * Disabled is `aria-disabled` rather than the `disabled` attribute, so the button stays in the tab
 * order and can say why with `disabledReason`, which shows as a tip on hover and focus and is the
 * button's description. Loading keeps the label and the width, draws a spinner in place of the
 * leading icon, and ignores presses, so a second press of a submit button does nothing. A press
 * that is ignored also stops the form's submit, including the one Enter sends from a field.
 *
 * `tone`, `wide` and `size="regular" | "small"` are the names from before the variants and map onto
 * them (`solid` to `primary`, `quiet` to `secondary`, `regular` to `lg`, `small` to `md`). `busy`
 * keeps its old meaning: presses are ignored and the cursor says so, with no spinner, for a button
 * that waits on an action another control started. New code uses the new names.
 */
import * as stylex from "@stylexjs/stylex";
import type { MouseEvent, ReactNode } from "react";
import { useId } from "react";
import type { ButtonCorners, ButtonSize, ButtonVariant } from "./buttonStyle.ts";
import { buttonStyle } from "./buttonStyle.ts";
import { focusRing } from "./focus.ts";
import { Spinner } from "./Spinner.tsx";
import { Tooltip } from "./Tooltip.tsx";
export interface ButtonProps {
  readonly children: ReactNode;
  /** Optional only for submit buttons, whose form handles the press. */
  readonly onClick?: (() => void) | undefined;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize | "regular" | "small";
  /** `pill` on the customer site, `rounded` in the clinic portal and the admin. */
  readonly corners?: ButtonCorners;
  /** "submit" lets a form run the browser's own validation before its submit handler. */
  readonly type?: "button" | "submit";
  /** Ignores presses and shows a spinner while the action this button started is under way. */
  readonly loading?: boolean;
  readonly disabled?: boolean;
  /** Why the button is disabled, in a few words: "Add a reason first". */
  readonly disabledReason?: string | undefined;
  /** Runs when someone presses the button while it is disabled, to take them to what is missing. */
  readonly onDisabledPress?: (() => void) | undefined;
  /** The id of text already on the page that says why, in place of a tip that would repeat it. */
  readonly describedBy?: string | undefined;
  readonly leadingIcon?: ReactNode;
  readonly trailingIcon?: ReactNode;
  /** Fills its container. `"phone"` fills it below 640px only, with a 44px target, for a dialog's footer. */
  readonly fullWidth?: boolean | "phone";
  /** Makes it a toggle: `true` is on, `false` is off, and leaving it out is a plain button. */
  readonly pressed?: boolean;
  readonly testId?: string;
  /** For a button whose visible label is shared with others, such as "Remove" on every row. */
  readonly ariaLabel?: string;
  /** Takes focus when the dialog that holds it opens, in place of the first control. */
  readonly initialFocus?: boolean;
  /** Before `variant`. `solid` is `primary` and `quiet` is `secondary`. */
  readonly tone?: "solid" | "quiet";
  /** Ignores presses with no spinner, while an action another control started is under way. */
  readonly busy?: boolean;
  /** Before `fullWidth`. */
  readonly wide?: boolean;
  /** No padding on the start side, for a tertiary button that sits in a column of text. */
  readonly flush?: boolean;
}
const SIZES = {
  regular: "lg",
  small: "md",
  lg: "lg",
  md: "md",
  sm: "sm"
} as const;
const LOADING = {
  lg: buttonStyle.lgLoading,
  md: buttonStyle.mdLoading,
  sm: buttonStyle.smLoading
} as const;

/** Full width everywhere, or below 640px only with a finger-sized height for its size step. */
function fillStyles(fills: boolean | "phone", step: "lg" | "md" | "sm") {
  if (fills === true) return buttonStyle.fullWidth;
  if (fills !== "phone") return null;
  return [buttonStyle.fullWidthPhone, step === "lg" && buttonStyle.phoneFloorLg, step === "md" && buttonStyle.phoneFloorMd];
}
export function Button({
  children,
  onClick,
  variant,
  size = "lg",
  corners = "pill",
  type = "button",
  loading,
  disabled = false,
  disabledReason,
  describedBy,
  onDisabledPress,
  leadingIcon,
  trailingIcon,
  fullWidth,
  pressed,
  testId,
  ariaLabel,
  initialFocus,
  tone,
  busy,
  wide,
  flush = false
}: ButtonProps) {
  const reasonId = useId();
  const look = variant ?? (tone === "quiet" ? "secondary" : "primary");
  const step = SIZES[size];
  const waiting = loading ?? false;
  const fills = fullWidth ?? wide ?? false;
  const inert = waiting || busy === true || disabled;
  const reason = disabled ? disabledReason : undefined;
  const press = (event: MouseEvent<HTMLButtonElement>) => {
    if (inert) {
      event.preventDefault();
      if (disabled) onDisabledPress?.();
    } else onClick?.();
  };
  const button = <button type={type} onClick={press} aria-disabled={inert || undefined} aria-busy={waiting || busy === true || undefined} aria-label={ariaLabel} aria-pressed={pressed} aria-describedby={reason === undefined ? describedBy : reasonId} data-testid={testId} data-initial-focus={initialFocus === true ? "true" : undefined} {...stylex.props(buttonStyle.base, corners === "pill" ? buttonStyle.pill : buttonStyle.rounded, buttonStyle[step], flush && buttonStyle.flush, waiting && leadingIcon === undefined && LOADING[step], fillStyles(fills, step), buttonStyle[look], pressed === true && buttonStyle.pressed, disabled && buttonStyle.disabled, (waiting || busy === true) && buttonStyle.loading, focusRing)}>
      {waiting ? <span {...stylex.props(buttonStyle.slot, leadingIcon === undefined && buttonStyle.spinnerAlone)}>
          <Spinner />
        </span> : leadingIcon !== undefined && <span {...stylex.props(buttonStyle.slot)}>{leadingIcon}</span>}
      <span {...stylex.props(buttonStyle.label)}>{children}</span>
      {trailingIcon !== undefined && <span {...stylex.props(buttonStyle.slot)}>{trailingIcon}</span>}
    </button>;
  if (reason === undefined) return button;
  return <Tooltip id={reasonId} text={reason} fill={fills}>
      {button}
    </Tooltip>;
}